import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useForm } from "@formspree/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORMSPREE_ID = "mqengrao";

export const Footer = () => {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [, submitToFormspree] = useForm(FORMSPREE_ID);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            // Fire backend (MongoDB + email) and Formspree in parallel
            await Promise.allSettled([
                axios.post(`${API}/newsletter`, { email }),
                submitToFormspree({ email, type: "newsletter", _subject: `New Subscriber — ${email}` }),
            ]);
            toast.success("You're in. Letters from the hills, once a month.");
            setEmail("");
        } catch (err) {
            console.error(err);
            toast.error("Hmm. That email didn't go through. Try again?");
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer
            data-testid="site-footer"
            className="bg-forest text-paper pt-16 md:pt-32 pb-10 overflow-x-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-4 md:px-10 lg:px-14">
                <div className="grid grid-cols-12 gap-8 md:gap-10 pb-16 md:pb-20 border-b border-paper/15">
                    <div className="col-span-12 md:col-span-6 reveal">
                        <p className="text-xs uppercase tracking-[0.28em] text-marigold mb-5">
                            — Letters from the hills
                        </p>
                        <h3 className="font-serif text-paper text-2xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                            One slow letter a month. <em className="italic text-marigold">No noise.</em>
                        </h3>
                        <p className="mt-5 text-paper/70 leading-relaxed text-sm md:text-base">
                            A short essay, a season's worth of festivals, and one homestay we've
                            fallen for. That's the entire deal.
                        </p>

                        <form onSubmit={subscribe} className="mt-8 flex w-full border-b border-paper/30 focus-within:border-marigold transition-colors">
                            <input
                                type="email"
                                required
                                data-testid="newsletter-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your email"
                                className="flex-1 min-w-0 bg-transparent text-paper placeholder:text-paper/40 py-3 focus:outline-none text-sm"
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="newsletter-submit"
                                className="shrink-0 text-[11px] uppercase tracking-[0.18em] text-marigold hover:text-paper transition-colors pl-3"
                            >
                                {busy ? "…" : "Subscribe →"}
                            </button>
                        </form>
                    </div>

                    <div className="col-span-12 sm:col-span-6 md:col-span-2 reveal">
                        <p className="text-[11px] uppercase tracking-[0.22em] text-marigold mb-5">Wander</p>
                        <ul className="space-y-3 text-paper/80 text-sm">
                            <li><a className="hover:text-marigold" href="#states">Eight States</a></li>
                            <li><a className="hover:text-marigold" href="#travellers">Travellers</a></li>
                            <li><a className="hover:text-marigold" href="#festivals">Festivals</a></li>
                            <li><a className="hover:text-marigold" href="#manifesto">Manifesto</a></li>
                        </ul>
                    </div>

                    <div className="col-span-12 sm:col-span-6 md:col-span-2 reveal">
                        <p className="text-[11px] uppercase tracking-[0.22em] text-marigold mb-5">Talk to us</p>
                        <ul className="space-y-3 text-paper/80 text-sm">
                            <li><a className="hover:text-marigold" href="mailto:hello@enei.in">hello@enei.in</a></li>
                            <li><a className="hover:text-marigold" href="#plan">Plan my trip</a></li>
                            <li>
                                <a
                                    className="inline-flex items-center gap-2 hover:text-[#25D366] transition-colors"
                                    href="https://wa.me/917002345186"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 shrink-0 text-[#25D366]">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                    WhatsApp us
                                </a>
                            </li>
                            <li>
                                <a
                                    className="inline-flex items-center gap-2 hover:text-[#E1306C] transition-colors"
                                    href="https://www.instagram.com/official_enei/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 shrink-0 text-[#E1306C]">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                                    </svg>
                                    Instagram
                                </a>
                            </li>
                            <li><a className="hover:text-marigold" href="#">Substack</a></li>
                        </ul>
                    </div>

                    <div className="col-span-12 md:col-span-2 reveal">
                        <p className="text-[11px] uppercase tracking-[0.22em] text-marigold mb-5">Care</p>
                        <ul className="space-y-3 text-paper/80 text-sm">
                            <li>Responsible Travel</li>
                            <li>Permits & Paperwork</li>
                            <li>FAQ</li>
                            <li>Press</li>
                        </ul>
                    </div>
                </div>

                <div className="pt-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <p className="font-serif italic text-paper/70 text-sm md:text-lg leading-relaxed break-words">
                        "The Northeast is not a checklist. It is a conversation."
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-paper/50 break-words">
                        © {new Date().getFullYear()} enei.in · Made with care in the hills
                    </p>
                </div>
            </div>
        </footer>
    );
};
